import React, { useState, useEffect, useRef } from 'react';
import { clickable } from './utils';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const CATEGORIES = ['General', 'Network', 'Active Directory', 'Azure', 'System', 'Monitoring', 'Other'];

function IconTerminal() {
  return <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/></svg>;
}
function IconCopy() {
  return <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>;
}
function IconEdit() {
  return <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
}
function IconTrash() {
  return <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>;
}
function IconPlus() {
  return <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
}

function categoryColor(cat) {
  const map = {
    'General':        '#6366f1',
    'Network':        '#0891b2',
    'Active Directory': '#7c3aed',
    'Azure':          '#2563eb',
    'System':         '#059669',
    'Monitoring':     '#d97706',
    'Other':          '#64748b',
  };
  return map[cat] || '#64748b';
}

function ScriptForm({ initial, onSave, onCancel }) {
  const [title,       setTitle]       = useState(initial?.title       || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [content,     setContent]     = useState(initial?.content     || '');
  const [category,    setCategory]    = useState(initial?.category    || 'General');
  const [saving,      setSaving]      = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    await onSave({ title, description, content, category });
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14, height: '100%' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10 }}>
        <input
          value={title} onChange={e => setTitle(e.target.value)}
          placeholder="Script title…"
          required
          style={{ fontFamily: 'DM Sans', fontSize: 15, fontWeight: 600, padding: '8px 12px',
            border: '1px solid var(--border)', borderRadius: 7, background: 'var(--surface)',
            color: 'var(--text)', outline: 'none' }}
        />
        <select value={category} onChange={e => setCategory(e.target.value)}
          style={{ fontFamily: 'DM Sans', fontSize: 12, padding: '8px 10px',
            border: '1px solid var(--border)', borderRadius: 7, background: 'var(--surface)',
            color: 'var(--text)', outline: 'none' }}>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <input
        value={description} onChange={e => setDescription(e.target.value)}
        placeholder="Short description (optional)…"
        style={{ fontFamily: 'DM Sans', fontSize: 13, padding: '7px 12px',
          border: '1px solid var(--border)', borderRadius: 7, background: 'var(--surface)',
          color: 'var(--text)', outline: 'none' }}
      />

      <textarea
        value={content} onChange={e => setContent(e.target.value)}
        placeholder="# Paste your PowerShell script here…"
        spellCheck={false}
        style={{ flex: 1, minHeight: 320, fontFamily: 'DM Mono, Consolas, monospace', fontSize: 13,
          lineHeight: 1.6, padding: '12px 14px',
          border: '1px solid var(--border)', borderRadius: 7,
          background: '#0f172a', color: '#e2e8f0', outline: 'none',
          resize: 'vertical', whiteSpace: 'pre', overflowX: 'auto' }}
      />

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancel}
          style={{ padding: '7px 16px', borderRadius: 7, border: '1px solid var(--border)',
            background: 'var(--surface-2)', color: 'var(--text)', fontFamily: 'DM Sans',
            fontSize: 13, cursor: 'pointer' }}>
          Cancel
        </button>
        <button type="submit" disabled={saving || !title.trim()}
          style={{ padding: '7px 18px', borderRadius: 7, border: 'none',
            background: 'var(--accent)', color: '#fff', fontFamily: 'DM Sans',
            fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? .7 : 1 }}>
          {saving ? 'Saving…' : (initial ? 'Save changes' : 'Add script')}
        </button>
      </div>
    </form>
  );
}

function ScriptDetail({ script, onEdit, onDelete }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(script.content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const color = categoryColor(script.category);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
              background: color + '22', color, border: `1px solid ${color}44` }}>
              {script.category || 'General'}
            </span>
          </div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text)', lineHeight: 1.3 }}>
            {script.title}
          </h2>
          {script.description && (
            <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5 }}>
              {script.description}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button onClick={onEdit}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px',
              border: '1px solid var(--border)', borderRadius: 7, background: 'var(--surface-2)',
              color: 'var(--text)', fontSize: 12, cursor: 'pointer', fontFamily: 'DM Sans' }}>
            <IconEdit /> Edit
          </button>
          <button onClick={onDelete}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px',
              border: '1px solid #fecaca', borderRadius: 7, background: '#fff1f2',
              color: '#dc2626', fontSize: 12, cursor: 'pointer', fontFamily: 'DM Sans' }}>
            <IconTrash /> Delete
          </button>
        </div>
      </div>

      {/* Code block */}
      <div style={{ flex: 1, position: 'relative', borderRadius: 10, overflow: 'hidden',
        border: '1px solid #1e293b', background: '#0f172a' }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 14px', background: '#1e293b', borderBottom: '1px solid #334155' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ display: 'flex', gap: 5 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#f59e0b', display: 'inline-block' }} />
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#10b981', display: 'inline-block' }} />
            </div>
            <span style={{ fontSize: 11, color: '#64748b', fontFamily: 'DM Mono, monospace' }}>PowerShell</span>
          </div>
          <button onClick={copy}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px',
              borderRadius: 5, border: '1px solid #334155',
              background: copied ? '#065f46' : '#1e293b',
              color: copied ? '#6ee7b7' : '#94a3b8',
              fontSize: 11, cursor: 'pointer', fontFamily: 'DM Sans', transition: 'all .15s' }}>
            <IconCopy />
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
        {/* Code */}
        <pre style={{ margin: 0, padding: '16px 18px', overflowY: 'auto', overflowX: 'auto',
          fontFamily: 'DM Mono, Consolas, monospace', fontSize: 13, lineHeight: 1.7,
          color: '#e2e8f0', whiteSpace: 'pre', maxHeight: 'calc(100vh - 280px)' }}>
          <code>{script.content || '# (empty script)'}</code>
        </pre>
      </div>
    </div>
  );
}

export default function ScriptsPage() {
  const [scripts,   setScripts]   = useState([]);
  const [selected,  setSelected]  = useState(null);
  const [mode,      setMode]      = useState('view');   // 'view' | 'new' | 'edit'
  const [search,    setSearch]    = useState('');
  const [catFilter, setCatFilter] = useState('All');
  const [loading,   setLoading]   = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = () => {
    fetch(`${API}/api/scripts`)
      .then(r => r.json())
      .then(data => { setScripts(data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = scripts.filter(s => {
    const q = search.toLowerCase();
    const matchSearch = !q || s.title.toLowerCase().includes(q)
      || s.description.toLowerCase().includes(q)
      || s.content.toLowerCase().includes(q);
    const matchCat = catFilter === 'All' || s.category === catFilter;
    return matchSearch && matchCat;
  });

  const handleSave = async (data) => {
    if (mode === 'new') {
      const r = await fetch(`${API}/api/scripts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const created = await r.json();
      setScripts(prev => [...prev, created].sort((a,b) => a.title.localeCompare(b.title)));
      setSelected(created);
    } else {
      await fetch(`${API}/api/scripts/${selected.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const updated = { ...selected, ...data };
      setScripts(prev => prev.map(s => s.id === selected.id ? updated : s));
      setSelected(updated);
    }
    setMode('view');
  };

  const handleDelete = async () => {
    await fetch(`${API}/api/scripts/${selected.id}`, { method: 'DELETE' });
    setScripts(prev => prev.filter(s => s.id !== selected.id));
    setSelected(null);
    setMode('view');
    setConfirmDelete(false);
  };

  const usedCategories = ['All', ...CATEGORIES.filter(c => scripts.some(s => s.category === c))];

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 56px)', overflow: 'hidden', fontFamily: 'DM Sans' }}>

      {/* ── Left panel: script list ── */}
      <div style={{ width: 280, flexShrink: 0, borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column', background: 'var(--surface)' }}>

        {/* Search + New */}
        <div style={{ padding: '12px 12px 8px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search scripts…"
              style={{ flex: 1, fontFamily: 'DM Sans', fontSize: 12, padding: '6px 10px',
                border: '1px solid var(--border)', borderRadius: 7, background: 'var(--surface-2)',
                color: 'var(--text)', outline: 'none' }} />
            <button onClick={() => { setSelected(null); setMode('new'); }}
              title="New script"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 32, height: 32, borderRadius: 7, border: 'none',
                background: 'var(--accent)', color: '#fff', cursor: 'pointer', flexShrink: 0 }}>
              <IconPlus />
            </button>
          </div>
          {/* Category chips */}
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {usedCategories.map(c => (
              <button key={c} onClick={() => setCatFilter(c)}
                style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 500,
                  cursor: 'pointer', fontFamily: 'DM Sans',
                  border: `1px solid ${catFilter === c ? categoryColor(c) : 'var(--border)'}`,
                  background: catFilter === c ? categoryColor(c) + '22' : 'transparent',
                  color: catFilter === c ? categoryColor(c) : 'var(--text-2)' }}>
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading && <div style={{ padding: 16, fontSize: 13, color: 'var(--text-2)' }}>Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div style={{ padding: 20, fontSize: 13, color: 'var(--text-2)', textAlign: 'center' }}>
              {scripts.length === 0 ? 'No scripts yet.\nClick + to add your first one.' : 'No matches.'}
            </div>
          )}
          {filtered.map(s => {
            const color = categoryColor(s.category);
            const isActive = selected?.id === s.id;
            return (
              <div key={s.id} {...clickable(() => { setSelected(s); setMode('view'); setConfirmDelete(false); })} aria-label={s.title}
                style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)',
                  background: isActive ? 'var(--accent-light, #eff6ff)' : 'transparent',
                  borderLeft: `3px solid ${isActive ? color : 'transparent'}`,
                  transition: 'background .1s' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 8,
                    background: color + '22', color }}>
                    {s.category || 'General'}
                  </span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {s.title}
                </div>
                {s.description && (
                  <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {s.description}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border)', fontSize: 11, color: 'var(--text-2)' }}>
          {filtered.length} script{filtered.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* ── Right panel ── */}
      <div style={{ flex: 1, padding: 24, overflowY: 'auto', background: 'var(--bg)' }}>

        {/* NEW or EDIT form */}
        {(mode === 'new' || mode === 'edit') && (
          <ScriptForm
            initial={mode === 'edit' ? selected : null}
            onSave={handleSave}
            onCancel={() => setMode(selected ? 'view' : 'view')}
          />
        )}

        {/* VIEW */}
        {mode === 'view' && selected && (
          <>
            {confirmDelete ? (
              <div style={{ padding: 20, borderRadius: 10, background: '#fff1f2',
                border: '1px solid #fecaca', maxWidth: 420 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#dc2626', marginBottom: 8 }}>Delete script?</div>
                <div style={{ fontSize: 13, color: '#7f1d1d', marginBottom: 16 }}>
                  "<b>{selected.title}</b>" will be permanently deleted.
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={handleDelete}
                    style={{ padding: '7px 16px', borderRadius: 7, border: 'none',
                      background: '#dc2626', color: '#fff', fontFamily: 'DM Sans',
                      fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    Yes, delete
                  </button>
                  <button onClick={() => setConfirmDelete(false)}
                    style={{ padding: '7px 16px', borderRadius: 7, border: '1px solid var(--border)',
                      background: 'var(--surface)', color: 'var(--text)', fontFamily: 'DM Sans',
                      fontSize: 13, cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <ScriptDetail
                script={selected}
                onEdit={() => setMode('edit')}
                onDelete={() => setConfirmDelete(true)}
              />
            )}
          </>
        )}

        {/* Empty state */}
        {mode === 'view' && !selected && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            height: '70%', gap: 14, color: 'var(--text-2)' }}>
            <div style={{ fontSize: 48, opacity: .4 }}><IconTerminal /></div>
            <div style={{ fontSize: 16, fontWeight: 600, opacity: .6 }}>PowerShell Script Library</div>
            <div style={{ fontSize: 13, opacity: .5, textAlign: 'center', maxWidth: 320 }}>
              Select a script from the list, or click <b>+</b> to add your first one.
            </div>
            <button onClick={() => setMode('new')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px',
                border: 'none', borderRadius: 8, background: 'var(--accent)', color: '#fff',
                fontFamily: 'DM Sans', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              <IconPlus /> New script
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
